import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const VisualReportDetail: React.FC<{
  heatNumber: string;
  pipeLengthM?: number;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, pipeLengthM = 12.18, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const visualMeasurements = measurements.filter((measurement) => measurement.reportType === 'VISUAL');
  const pipeLengthMm = pipeLengthM * 1000;
  const effectiveFrequency = metadata?.frequency || frequency || 'per_pipe';
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['No project limits', 'Configure project limits to show live values', true],
  ]);
  const depthLimitMax = getProjectLimitMax(projectLimitFields, 'visual_depth_pct_wt', 12.5);
  const lightingMinLux = getProjectLimitMin(projectLimitFields, 'visual_lighting_lux', 500);
  const defects = visualMeasurements.map((measurement) => {
    const type = measurement.visualDefectType || 'scale';
    const depth = Number(measurement.visualDepthPctWt) || 0;
    const disposition = measurement.visualDisposition || 'accept';
    const autoFail = type === 'arc_strike' || type === 'crack' || (depth > depthLimitMax && disposition !== 'repaired');
    return {
      id: measurement.visualDefectId || measurement.id,
      type,
      posMm: Number(measurement.visualPositionMm) || 0,
      clock: measurement.visualClock || '5:00',
      depthPctWt: depth,
      disposition,
      status: autoFail ? 'FAIL' : measurement.status,
      measurement,
    };
  });
  const lightingMeasurement = visualMeasurements.find((measurement) => {
    const lighting = Number(measurement.visualLightingLux);
    return Number.isFinite(lighting) && lighting > 0;
  });
  const hasLightingReading = Boolean(lightingMeasurement);
  const lightingLux = Number(lightingMeasurement?.visualLightingLux);
  const lightingStatus = hasLightingReading
    ? (lightingLux >= lightingMinLux ? 'PASS' : 'FAIL')
    : 'PENDING_REVIEW';
  const repairedCount = defects.filter((defect) => defect.disposition === 'repaired').length;
  const rejectedCount = defects.filter((defect) => defect.status === 'FAIL').length;
  const clockToY = (clock: string) => {
    const [hourRaw, minuteRaw = '0'] = clock.split(':');
    const hour = Number(hourRaw) || 0;
    const minute = Number(minuteRaw) || 0;
    const angle = (((hour % 12) + minute / 60) / 12) * 2 * Math.PI;
    return 50 - Math.cos(angle) * 30;
  };
  const autoFailRules = [
    { type: 'arc_strike', desc: 'Any arc strike fails the pipe (API 5L mandatory).', count: defects.filter((defect) => defect.type === 'arc_strike').length },
    { type: 'crack', desc: 'Any crack fails the pipe regardless of depth.', count: defects.filter((defect) => defect.type === 'crack').length },
    { type: 'depth > 12.5 % WT', desc: 'Unless disposition is repaired.', count: defects.filter((defect) => defect.depthPctWt > depthLimitMax && defect.disposition !== 'repaired').length },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Catalogued defects on pipe</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">
              Coverage: both ends + full body{hasLightingReading ? ` · lighting ${lightingLux} lux` : ''}
            </div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('VISUAL')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Defect
          </Button>
        </div>

        <div>
          <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Auto-fail conditions</h3>
          <div className="mt-1 text-[12px] text-heritage-grey">Defect types that fail the pipe regardless of depth</div>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            {autoFailRules.map((rule) => (
              <Card key={rule.type} className="border-[#E2E5EA]">
                <CardContent className="p-4">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{rule.type}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className={cn('font-heading text-[24px] font-semibold', rule.count > 0 ? 'text-readable-red' : 'text-galvanizing-green')}>{rule.count}</span>
                    <span className="text-[11px] text-heritage-grey">found</span>
                  </div>
                  <div className="mt-2 text-[11px] text-heritage-grey">{rule.desc}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {hasLightingReading && (
          <Card className="overflow-hidden border-[#E2E5EA]">
            <CardContent className="p-0">
              <SectionHead title="Lighting & inspector" />
              <TensileResultRow label="Lighting at surface" formula={`min ${lightingMinLux} lux`} value={lightingLux} unit="lux" min={Math.max(0, lightingMinLux - 100)} max={Math.max(750, lightingMinLux + 250)} lo={lightingMinLux} status={lightingStatus} />
            </CardContent>
          </Card>
        )}

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Defect detail" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Pos (mm)</th>
                  <th className="px-4 py-3">Clock</th>
                  <th className="px-4 py-3 text-right">Depth % WT</th>
                  <th className="px-4 py-3">Disposition</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {!defects.length && (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-[12px] text-heritage-grey">
                      No visual defects saved yet. Add a defect to populate this report.
                    </td>
                  </tr>
                )}
                {defects.map((defect) => (
                  <tr key={defect.id} onClick={() => defect.measurement && onEditMeasurement?.(defect.measurement)} className={cn(defect.measurement && 'cursor-pointer hover:bg-page-bg')}>
                    <td className="px-4 py-3 font-mono font-semibold">{defect.id}</td>
                    <td className="px-4 py-3">{defect.type}</td>
                    <td className="px-4 py-3 text-right font-mono">{defect.posMm.toLocaleString()}</td>
                    <td className="px-4 py-3 font-mono">{defect.clock}</td>
                    <td className="px-4 py-3 text-right font-mono">{defect.depthPctWt.toFixed(1)}</td>
                    <td className="px-4 py-3">{defect.disposition}</td>
                    <td className="px-4 py-3"><StatusBadge status={defect.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {defect.measurement && (
                        <ItemActionButtons measurement={defect.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Defect location map" subtitle="Positions are plotted by pipe length and clock orientation" />
            <div className="p-4">
              <div className="relative h-24 overflow-hidden rounded-full border border-[#D9DEE6] bg-gradient-to-b from-white via-[#EEF0F3] to-[#E2E5EA] shadow-inner">
                {[0, 0.25, 0.5, 0.75, 1].map((point) => (
                  <div key={point} className="absolute bottom-0 top-0 w-px bg-[#D9DEE6]/70" style={{ left: `${point * 100}%` }} />
                ))}
                <span className="absolute bottom-3 left-5 font-mono text-[10px] text-heritage-grey">End 1</span>
                <span className="absolute bottom-3 right-5 font-mono text-[10px] text-heritage-grey">End 2</span>
                <span className="absolute right-6 top-2 font-mono text-[10px] text-heritage-grey">seam · 12:00</span>
                {defects.map((defect) => (
                  <button
                    key={defect.id}
                    type="button"
                    title={`${defect.id} ${defect.type} ${defect.depthPctWt}% WT`}
                    onClick={() => defect.measurement && onEditMeasurement?.(defect.measurement)}
                    className={cn(
                      'absolute h-2.5 w-2.5 rounded-full border-2 border-white shadow-sm',
                      defect.status === 'FAIL' ? 'bg-readable-red' : defect.status === 'PENDING_REVIEW' ? 'bg-[#B38600]' : 'bg-galvanizing-green',
                      defect.measurement ? 'cursor-pointer' : 'cursor-default'
                    )}
                    style={{
                      left: `calc(${Math.max(1, Math.min(99, pipeLengthMm > 0 ? (defect.posMm / pipeLengthMm) * 100 : 0))}% - 5px)`,
                      top: `calc(${clockToY(defect.clock)}% - 5px)`,
                    }}
                  />
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] text-heritage-grey">0 mm</span>
                {['scale', 'groove', 'pit', 'arc_strike', 'crack'].map((type) => (
                  <span key={type} className="rounded-full border border-[#E2E5EA] bg-page-bg px-2 py-1 font-mono text-[10px] text-text-grey">{type}</span>
                ))}
                <span className="ml-auto text-[11px] text-heritage-grey">{defects.length} catalogued · {repairedCount} repaired · {rejectedCount} rejected</span>
                <span className="font-mono text-[10px] text-heritage-grey">{pipeLengthM.toFixed(2)} m</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows
              rows={[
                ['Frequency', effectiveFrequency, true],
                ['Heat number', heatNumber || '-', true],
                ['Defects', `${defects.length} saved`, true],
              ]}
            />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
